import { FC, useState } from "react";
import { useAffiliatePaymentsPerMonth } from "@/queries";
import Collapse from "@mui/material/Collapse";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { AffiliateEarning } from "@/types";
import { formatDate, getAffiliatePaymentDescription } from "@/utils";

const AffiliateEarningRow: FC<AffiliateEarning & { affiliateId: string }> = ({
  month,
  year,
  amountCents,
  affiliateId,
}) => {
  const [open, setOpen] = useState(false);

  const {
    affiliatePaymentsPerMonth,
    affiliatePaymentsPerMonthLoading,
    affiliatePaymentsPerMonthError,
  } = useAffiliatePaymentsPerMonth({
    affiliateId: open ? affiliateId : "", // only fetch payments when this row is expanded by conditionally passing affiliateId
    month,
    year,
    onlyPaid: false,
  });

  return (
    <>
      <TableRow>
        <TableCell align="left">
          {dayjs()
            .year(year)
            .month(month - 1)
            .format("MMMM YYYY")}
        </TableCell>
        <TableCell align="left">{(amountCents / 100).toFixed(2)}</TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {affiliatePaymentsPerMonthLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={120}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : affiliatePaymentsPerMonthError ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={120}
                >
                  <Typography>Something went wrong</Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date of Transaction</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {affiliatePaymentsPerMonth.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>
                          {formatDate(payment.createdAt, "DD.MM.YYYY")}
                        </TableCell>
                        <TableCell>
                          {getAffiliatePaymentDescription(payment)}
                        </TableCell>
                        <TableCell align="right">
                          {(payment.amountCents / 100).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export { AffiliateEarningRow };
